import { useState } from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AudiqWordmark,
  AudiqMark,
  EnvChrome,
  ThemeToggle,
  RuleBicolor,
  type InstanceEnv,
} from '@audiq/ui';
import { api } from './lib/api';

interface SidebarItem {
  label: string;
  to: string;
  comingSoon?: boolean;
}

const SIDEBAR_ITEMS: SidebarItem[] = [
  { label: 'Dashboard', to: '/' },
  { label: 'Flow', to: '/flows' },
  { label: 'Conversazioni', to: '/encounters' },
  { label: 'Pazienti', to: '/patients' },
  { label: 'Consumi', to: '/usage' },
  { label: 'Integrazioni', to: '/integrations' },
  { label: 'Configurazione', to: '/settings' },
];

export function AppShell() {
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const healthQuery = useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const res = await fetch('/api/health', { credentials: 'include' });
      return res.json();
    },
  });

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await api.logout();
      navigate('/login', { replace: true });
    } finally {
      setLoggingOut(false);
    }
  }

  const env: InstanceEnv = (healthQuery.data?.instance_env ?? 'production') as InstanceEnv;

  return (
    <div
      className="flex flex-1 flex-col"
      style={{ background: 'var(--surface)', color: 'var(--ink)' }}
    >
      <EnvChrome env={env} className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3">
          <AudiqWordmark size="sm" inverse={env !== 'production'} />
          <span
            className="mono"
            style={{ color: env === 'production' ? 'var(--mute)' : 'var(--ink)' }}
          >
            Admin · {env}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span
            className="text-sm"
            style={{ color: env === 'production' ? 'var(--mute)' : 'var(--ink)' }}
          >
            {meQuery.data?.user.email}
          </span>
          <ThemeToggle size="sm" />
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="rounded-md border px-3 py-1.5 text-sm transition disabled:opacity-50"
            style={{
              background: 'var(--paper)',
              borderColor: 'var(--line)',
              color: 'var(--ink)',
            }}
          >
            {loggingOut ? 'Logout…' : 'Logout'}
          </button>
        </div>
      </EnvChrome>

      {meQuery.data?.user.must_setup_totp && !meQuery.data.user.totp_enabled && (
        <div
          className="px-6 py-2 text-sm"
          style={{
            background: 'color-mix(in oklab, var(--accent) 14%, transparent)',
            borderBottom: '1px solid var(--line)',
            color: 'var(--ink)',
          }}
        >
          ⚠ Configura 2FA{' '}
          {meQuery.data.user.grace_until
            ? `entro il ${new Date(meQuery.data.user.grace_until).toLocaleDateString('it-IT')}`
            : 'subito'}
          .{' '}
          <Link
            to="/settings/2fa-setup"
            className="underline"
            style={{ color: 'var(--primary-2)' }}
          >
            Inizia setup →
          </Link>
        </div>
      )}
      {meQuery.data?.user.must_change_password && (
        <div
          className="px-6 py-2 text-sm"
          style={{
            background: 'color-mix(in oklab, var(--danger) 12%, transparent)',
            borderBottom: '1px solid var(--line)',
            color: 'var(--ink)',
          }}
        >
          ⚠ La tua password è temporanea.{' '}
          <Link
            to="/settings/security?force_change=1"
            className="underline"
            style={{ color: 'var(--danger)' }}
          >
            Cambiala ora →
          </Link>
        </div>
      )}

      <div className="flex flex-1">
        <aside
          className="flex w-60 shrink-0 flex-col border-e"
          style={{
            background: 'var(--paper)',
            borderColor: 'var(--line)',
          }}
        >
          <div className="flex items-center gap-3 px-5 pt-5 pb-3">
            <AudiqMark size={36} state="idle" />
            <AudiqWordmark size="md" />
          </div>

          <RuleBicolor />

          <nav className="flex-1 p-3">
            <ul className="space-y-0.5">
              {SIDEBAR_ITEMS.map((item) => (
                <li key={item.to}>
                  {item.comingSoon ? (
                    <span
                      className="block w-full cursor-not-allowed rounded-md px-3 py-2 text-start text-sm"
                      style={{ color: 'var(--mute)', opacity: 0.6 }}
                      title="Disponibile nei prossimi sprint"
                    >
                      {item.label}
                      <span className="ms-2 mono">soon</span>
                    </span>
                  ) : (
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      className={({ isActive }) =>
                        `block w-full rounded-md px-3 py-2 text-start text-sm transition`
                      }
                      style={({ isActive }) => ({
                        background: isActive ? 'var(--soft)' : 'transparent',
                        color: isActive ? 'var(--primary-2)' : 'var(--ink)',
                        fontWeight: isActive ? 600 : 400,
                      })}
                    >
                      {item.label}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="px-5 pb-4 mono" style={{ color: 'var(--mute)' }}>
            Powered by Audiq · Arkenù snc
          </div>
        </aside>

        <main className="flex-1 p-8">
          <Outlet context={{ health: healthQuery.data, me: meQuery.data }} />
        </main>
      </div>
    </div>
  );
}

/** Pagina di home (dashboard placeholder) montata su `/`. */
export function DashboardHome() {
  const healthQuery = useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const res = await fetch('/api/health', { credentials: 'include' });
      return res.json();
    },
  });
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mono mb-2" style={{ color: 'var(--mute)' }}>
        Dashboard
      </div>
      <h2 className="serif text-4xl" style={{ color: 'var(--ink)' }}>
        Audiq ascolta prima di te.
      </h2>
      <p className="mt-3" style={{ color: 'var(--mute)' }}>
        Console di amministrazione. La configurazione dei flow è disponibile in Sprint 2; la
        conversation engine arriva con lo Sprint 3.
      </p>

      <RuleBicolor className="my-8" />

      <section
        className="audiq-dog-ear rounded-md border p-6"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
      >
        <h3 className="mono mb-3" style={{ color: 'var(--mute)' }}>
          Stato sistema
        </h3>
        <dl className="grid grid-cols-2 gap-y-2 text-sm" style={{ color: 'var(--ink)' }}>
          <dt style={{ color: 'var(--mute)' }}>Database</dt>
          <dd className="font-mono">{healthQuery.data?.db ?? '…'}</dd>
          <dt style={{ color: 'var(--mute)' }}>Versione</dt>
          <dd className="font-mono">{healthQuery.data?.version ?? '…'}</dd>
          <dt style={{ color: 'var(--mute)' }}>Uptime</dt>
          <dd className="font-mono">{healthQuery.data?.uptime_seconds ?? '…'}s</dd>
          <dt style={{ color: 'var(--mute)' }}>Ambiente</dt>
          <dd className="font-mono">{healthQuery.data?.instance_env ?? '…'}</dd>
          <dt style={{ color: 'var(--mute)' }}>Utente</dt>
          <dd>{meQuery.data?.user.email}</dd>
          <dt style={{ color: 'var(--mute)' }}>Ruolo</dt>
          <dd>{meQuery.data?.user.role}</dd>
        </dl>
      </section>
    </div>
  );
}
